import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Text from '../components/elements/TextComponent';
import Button from '../components/elements/Button';
import ImageComponent from '../components/elements/ImageComponent';
import userAtDesk from '../assets/userAtDesk.svg';

/* ========================= Embed config (5 joke suggests) ========================= */
const EMBED_SRC =
  "https://api.comechatwithme.ai/chat/embed/v2?botId=68c1a6f8557cd025af1e8101&name=Giggles&mode=inline&variant=default&theme=%23B04BFF&radius=16px&welcome=Hi%20I%27m%20Giggles%21%20Ask%20me%20for%20a%20joke%2C%20pun%20or%20for%20something%20funny&launcher=true&w=320&h=540&font=Inter%2C%20system-ui%2C%20sans-serif&fontUrl=https%3A%2F%2Ffonts.googleapis.com%2Fcss2%3Ffamily%3DInter%3Awght%40400%3B600%26display%3Dswap&allowSession=false&suggest=Give%20me%20three%20clever%20coffee%20puns&suggest=Roast%20my%20alarm%20clock%20%28keep%20it%20clean%29";


/* ================================ Small helpers ================================ */
function ChatEmbed({
  style,
  inline = true,
}: { style?: React.CSSProperties; inline?: boolean }) {
  const base: React.CSSProperties = { border: 0, display: 'block' };
  const inlineStyle: React.CSSProperties = { width: '100%', height: 540 };
  const fixedStyle: React.CSSProperties  = { position: 'fixed', right: 16, bottom: 16, width: 320, height: 540, zIndex: 2147483647 };
  return (
    <iframe
      title="Giggles Chat"
      src={EMBED_SRC}
      style={{ ...base, ...(inline ? inlineStyle : fixedStyle), ...style }}
      scrolling="no"
    />
  );
}

const BotCard: React.FC<{
  title: string;
  desc: string;
  badge?: string;
  onClick?: () => void;
}> = ({ title, desc, badge, onClick }) => (
  <motion.div
    whileHover={{ y: -2 }}
    transition={{ duration: 0.2 }}
    style={{
      border: '1px solid var(--bd, #e5e7eb)',
      borderRadius: 16,
      padding: 16,
      background: '#fff',
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
      cursor: onClick ? 'pointer' : 'default',
    }}
    onClick={onClick}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <div
        style={{
          width: 28, height: 28, borderRadius: 999,
          background: 'var(--primary, #8b2cd7)', display: 'grid', placeItems: 'center',
          color: '#fff', fontWeight: 700, fontSize: 13,
        }}
      >
        {title.charAt(0).toUpperCase()}
      </div>
      <Text text={title} fontSize="15px" fontWeight="700" lineHeight="18px" color="#111827" />
      {badge && (
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: 'Montserrat',
            fontSize: 12,
            color: 'var(--primary, #8b2cd7)',
            background: 'rgba(139,44,215,0.08)',
            border: '1px solid rgba(139,44,215,0.25)',
            padding: '3px 8px',
            borderRadius: 999,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {badge}
        </span>
      )}
    </div>
    <Text text={desc} fontSize="13px" fontWeight="400" lineHeight="20px" color="#374151" textAlign="left" />
  </motion.div>
);

/* =================================== Page =================================== */
const MobileHome: React.FC = () => {
  const navigate = useNavigate();

  // Dynamic viewport + keyboard-safe padding like your original mobile
  const [viewportHeight, setViewportHeight] = useState('100vh');
  const [bottomPadding, setBottomPadding] = useState(20);
  const [imageSize, setImageSize] = useState({ width: 160, height: 160 });

  useEffect(() => {
    const updateImageSize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const baseSize = Math.min(screenWidth * 0.45, screenHeight * 0.3);
      const clampedSize = Math.max(140, Math.min(baseSize, 220));
      setImageSize({ width: clampedSize, height: clampedSize });
    };
    updateImageSize();
    window.addEventListener('resize', updateImageSize);
    return () => window.removeEventListener('resize', updateImageSize);
  }, []);

  useEffect(() => {
    const updateHeightAndPadding = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(`${vh}px`);
      const full = window.innerHeight;
      const keyboardHeight = full - vh;
      setBottomPadding(keyboardHeight > 150 ? 0 : 20);
    };
    updateHeightAndPadding();
    window.visualViewport?.addEventListener('resize', updateHeightAndPadding);
    window.addEventListener('resize', updateHeightAndPadding);
    return () => {
      window.visualViewport?.removeEventListener('resize', updateHeightAndPadding);
      window.removeEventListener('resize', updateHeightAndPadding);
    };
  }, []);

  // Scroll container + chevron logic (mirrors desktop behavior)
  const scrollerRef = useRef<HTMLDivElement>(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.98 }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
      style={{ height: viewportHeight, fontFamily: 'Montserrat' }}
    >
      {/* Scrollable viewport (full height) */}
      <div
        ref={scrollerRef}
        style={{
          height: '100%',
          overflowY: 'auto',
          scrollSnapType: 'y mandatory', // snap each full screen
          WebkitOverflowScrolling: 'touch',
          background: '#fff',
        }}
      >
        {/* ============================= Screen 1 (Hero) ============================= */}
        <section style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Top Purple 50% */}
          <div
            style={{
              backgroundColor: 'var(--primary)',
              flex: 1,
              padding: '24px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <ImageComponent
              src={userAtDesk}
              center
              style={{
                width: `${imageSize.width}px`,
                height: `${imageSize.height}px`,
                objectFit: 'contain',
              }}
            />
            <div>
              <Text text="ComeChatWithMe.ai" fontSize="24px" fontWeight="700" color="#ffffff" textAlign="center" />
              <div style={{ marginTop: 8 }}>
                <Text text="Create and share personalized assistants effortlessly." fontSize="14px" fontWeight="400" color="#ffffff" textAlign="center" />
              </div>
            </div>
          </div>

          {/* Bottom White 50% */}
          <div
            style={{
              backgroundColor: '#ffffff',
              flex: 1,
              padding: '24px 20px',
              paddingBottom: bottomPadding + 20,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              overflowY: 'auto',
            }}
          >
            <Text
              text="Customize Intelligent Assistants"
              fontSize="20px"
              fontWeight="700"
              color="#030303"
              textAlign="left"
            />
            <div style={{ height: 10 }} />
            <Text
              text="Create branded AI assistants in minutes—no ML required."
              fontSize="14px"
              fontWeight="400"
              lineHeight="20px"
              color="#030303"
              textAlign="left"
            />
            <Text
              text="Connect your content (PDFs, docs, FAQs), set instructions and tone, and tailor capabilities to your use case."
              fontSize="14px"
              fontWeight="400"
              lineHeight="20px"
              color="#030303"
              textAlign="left"
            />
            <Text
              text="Embed with a single iframe, script tag, or React component. Choose inline chat or a floating launcher, and match your brand with theme and fonts."
              fontSize="14px"
              fontWeight="400"
              lineHeight="20px"
              color="#030303"
              textAlign="left"
            />
            <Text
              text="Track engagement and iterate safely with session controls and suggestions."
              fontSize="14px"
              fontWeight="400"
              lineHeight="20px"
              color="#030303"
              textAlign="left"
            />

            <div style={{ marginTop: 20 }}>
              <Button label="Get Started" width="100%" height="42px" onClick={() => navigate('/login')} />
            </div>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 16 }}>
              <a href="/termsofservice" style={{ color: 'var(--primary)', textDecoration: 'underline', fontSize: 14 }}>
                Terms of Service
              </a>
              <a href="/privacypolicy" style={{ color: 'var(--primary)', textDecoration: 'underline', fontSize: 14 }}>
                Privacy Policy
              </a>
            </div>
          </div>
        </section>

        {/* Global chevron overlay (down until bottom, then up)
        {isScrollable && (
          isAtBottom
            ? <ArrowCue direction="up" onClick={scrollOneViewportUp} fixed />
            : <ArrowCue direction="down" onClick={scrollOneViewportDown} fixed />
        )} */}

        {/* ======================= Screen 2 (Bots Showcase) ======================= */}
        <section style={{ minHeight: '100vh', borderTop: '1px solid #eef2f7' }}>
          <div style={{ padding: '32px 20px', maxWidth: 700, margin: '0 auto' }}>
            <Text
              text="Create, Embed, and Share Bots — in Minutes"
              fontSize="22px"
              fontWeight="700"
              lineHeight="28px"
              color="#030303"
              textAlign="left"
            />
            <div style={{ height: 10 }} />
            <Text
              text="Pick a template, upload your files (PDFs, images, docs), and instantly get an embeddable assistant. Customize the theme to match your brand and share with a single link."
              fontSize="14px"
              fontWeight="400"
              lineHeight="22px"
              color="#111827"
              textAlign="left"
            />
            <div style={{ height: 16 }} />
            <div style={{ display: 'grid', gap: 12 }}>
              <BotCard
                title="Resume Bot"
                badge="Popular"
                desc="Embed a bot trained on your resume to answer recruiter questions in your tone. Share as a link or iframe."
                onClick={() => navigate('/login')}
              />
              <BotCard
                title="Site Assistant"
                desc="Turn your docs/FAQ into a helpful on-site assistant. Add a branded launcher and track engagement."
                onClick={() => navigate('/login')}
              />
              <BotCard
                title="Canvas Bot"
                desc="Create a focused bot around any uploaded info or brief. Great for portfolios, pitch decks, and demos."
                onClick={() => navigate('/login')}
              />
            </div>
            <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Button label="Build a Bot" height="42px" width="100%" onClick={() => navigate('/login')} />
              <Button
                label="View Examples"
                height="42px"
                width="100%"
                onClick={() => navigate('/login')}
                style={{ background: '#fff', color: 'var(--primary)', border: '1px solid var(--primary)' }}
              />
            </div>
          </div>
        </section>

        {/* ========================= Screen 3 (Embed Demo) ========================= */}
        <section style={{ minHeight: '100vh', borderTop: '1px solid #eef2f7' }}>
          <div style={{ padding: '32px 20px', maxWidth: 700, margin: '0 auto' }}>
            {/* Inline iframe demo */}
            <div
              style={{
                background: 'linear-gradient(0deg, rgba(139,44,215,0.06), rgba(139,44,215,0.06))',
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <ChatEmbed inline />
            </div>

            <Text
              text="Customize Every Detail of Your Embed"
              fontSize="22px"
              fontWeight="700"
              lineHeight="28px"
              color="#030303"
              textAlign="left"
            />
            <div style={{ height: 10 }} />
            <Text
              text="Brand the chat via simple URL params: set theme color, fonts, radius, launcher behavior, size, welcome text, and preset suggestions. Preview updates instantly."
              fontSize="14px"
              fontWeight="400"
              lineHeight="22px"
              color="#111827"
              textAlign="left"
            />
            <ul style={{ margin: '10px 0 0 18px', color: '#111827', fontFamily: 'Montserrat', fontSize: 13, lineHeight: '22px' }}>
              <li>
                Theme, font & radius: <code>theme</code>, <code>font</code>, <code>fontUrl</code>, <code>radius</code>
              </li>
              <li>
                Placement & size: <code>mode</code>/<code>launcher</code>, <code>w</code>, <code>h</code>
              </li>
              <li>
                Onboarding & session: <code>welcome</code>, multiple <code>suggest</code>, <code>allowSession</code>
              </li>
            </ul>

            <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Button label="Customize Embed" height="42px" width="100%" onClick={() => navigate('/login')} />
              <Button
                label="View Embed Docs"
                height="42px"
                width="100%"
                onClick={() => navigate('/login')}
                style={{ background: '#fff', color: 'var(--primary)', border: '1px solid var(--primary)' }}
              />
            </div>
          </div>
        </section>
      </div>
    </motion.div>
  );
};

export default MobileHome;
